import React from 'react';
import { NumberField } from '@/components/inspector-fields/NumberField/NumberField';
import { InfoTooltip } from '@/components/tooltips/InfoTooltip/InfoTooltip';
import clsx from 'clsx';
import type { ContainerProps } from '@builder4/shared/components/Container';

import { useNode } from '@craftjs/core';
import { useBreakpointAwareProp } from '@/hooks/useBreakpointAwareProp';

// Props for the slider field
interface NodeZindexSettings {
  className?: string; // extra class for wrapper
  classNameInput?: string; // extra class for input
  step?: number; // step size
  suffix?: string; // text after number (like px)
  allowDecimal?: boolean; // allow decimals
}

export const NodeZIndexSettingsV2: React.FC<NodeZindexSettings> = ({
  className,
  classNameInput,
  step,
  suffix,
  allowDecimal = false,
}) => {
  const actualStep = step ?? (allowDecimal ? 0.01 : 1); // step size
  const {
    nodeProps,
    displayName,
    actions: { setProp },
  } = useNode((node) => ({
    nodeProps: node.data.props,
    displayName: node.data.displayName,
  }));

  const { getBPProp, setBPProp } = useBreakpointAwareProp(displayName);
  const zIndex = getBPProp(nodeProps, 'zIndex') ?? 0;

  const handleChange = (val: number) => {
    setProp((props: ContainerProps) => {
      setBPProp(props, 'zIndex', val);
    }, 500);
  };

  return (
    <div className={clsx('flex items-center justify-between gap-3.5', className)}>
      <div className="flex items-center gap-1">
        <p className="text-button-sm font-medium text-neutral-500">{'Z Index'}</p>
        <InfoTooltip
          text="Controls stacking order of elements. Higher numbers appear in front of lower numbers. Use this to control which elements appear on top when they overlap."
          ariaLabel="Z-index information"
        />
      </div>
      <div className="ml-auto flex max-w-[128px] items-center gap-3">
        <NumberField
          value={zIndex} // input always gets latest value
          onChange={handleChange} // input changes update value (slider will update via props)
          min={-1}
          max={10}
          step={actualStep}
          suffix={suffix}
          allowDecimal={allowDecimal}
          className={classNameInput}
          dataTestId="z-index-input"
        />
      </div>
    </div>
  );
};
